/**
 * AGC: Anticipate Payout Admin Component
 * AGC: Allows owner to trigger early payout finalization for staking revenue
 * AGC: UPDATED 2025-11-28 - Added marketplace revenue distribution before payout
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useMemo, useEffect } from 'react'
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi'
import { parseEther, formatEther, formatUnits } from 'viem'
import { useContractOwner } from '../../hooks/useContractOwner'
import { buildBackendUrl } from '../../utils/backendUrl'

// AGC: Marketplace revenue status interface
interface MarketplaceRevenueStatus {
  initialized: boolean
  cronRunning: boolean
  walletAddress: string | null
  contracts: {
    buff1155?: {
      address: string
      ethBalance: string
      ogbBalance: string
      usdcBalance: string
      hasRevenue: boolean
    }
    classicPotion1155?: {
      address: string
      ethBalance: string
      ogbBalance: string
      usdcBalance: string
      hasRevenue: boolean
    }
  }
}
// AGC: StakingManagerV4 contract address (to read revenue share address)
const STAKING_MANAGER_ADDRESS = (import.meta.env.VITE_STAKING_MANAGER_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

// AGC: StakingRevenueShare contract address from environment (fallback)
// AGC: If not set, will be read from StakingManager contract
const REVENUE_SHARE_ADDRESS = (import.meta.env.VITE_STAKING_REVENUE_SHARE_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

// AGC: StakingRevenueShare V2 (old contract - has Week 99 StakingMirror rewards)
const REVENUE_SHARE_V2_ADDRESS = '0x5a7CB972cf3aBA7d3ab5d7efac892A5721ae59eE' as `0x${string}`

// AGC: StakingMirror contract address
const STAKING_MIRROR_ADDRESS = (import.meta.env.VITE_STAKING_MIRROR_ADDRESS || '0x0a89D5e0c759A87dB0a1Bd6EaaE81C97c3744A68') as `0x${string}`

// AGC: StakingManagerV4 ABI (to read revenue share address)
const STAKING_MANAGER_ABI = [
  {
    name: 'revenueShareContract',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  }
] as const

// AGC: StakingRevenueShare ABI (anticipate payout functions + claimRewardsForContract)
const REVENUE_SHARE_ABI = [
  {
    name: 'anticipatePayout',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  },
  {
    name: 'anticipatePayoutForWeek',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'week', type: 'uint256' }],
    outputs: []
  },
  {
    name: 'getCurrentWeek',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'weeklyRevenue',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: '', type: 'uint256' }],
    outputs: [
      { name: 'ethAmount', type: 'uint256' },
      { name: 'ogbAmount', type: 'uint256' },
      { name: 'usdcAmount', type: 'uint256' },
      { name: 'finalized', type: 'bool' }
    ]
  },
  {
    name: 'claimRewardsForContract',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'user', type: 'address' },
      { name: 'recipient', type: 'address' },
      { name: 'week', type: 'uint256' }
    ],
    outputs: []
  },
  {
    name: 'getPendingRewards',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'user', type: 'address' },
      { name: 'week', type: 'uint256' }
    ],
    outputs: [
      { name: 'ethAmount', type: 'uint256' },
      { name: 'ogbAmount', type: 'uint256' },
      { name: 'usdcAmount', type: 'uint256' }
    ]
  }
] as const

export const AnticipatePayout: React.FC = () => {
  const { address, isConnected } = useAccount()
  const { writeContractAsync, isPending } = useWriteContract()
  const [pendingTxHash, setPendingTxHash] = useState<`0x${string}` | null>(null)
  const [targetWeek, setTargetWeek] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // AGC: Marketplace revenue distribution state
  const [marketplaceRevenue, setMarketplaceRevenue] = useState<MarketplaceRevenueStatus | null>(null)
  const [isLoadingMarketplace, setIsLoadingMarketplace] = useState(false)
  const [isDistributing, setIsDistributing] = useState(false)
  const [distributeSuccess, setDistributeSuccess] = useState<string | null>(null)
  const [distributeError, setDistributeError] = useState<string | null>(null)
  
  // AGC: StakingMirror claim state
  const [isClaimingMirror, setIsClaimingMirror] = useState(false)
  const [mirrorClaimSuccess, setMirrorClaimSuccess] = useState<string | null>(null)
  const [mirrorClaimError, setMirrorClaimError] = useState<string | null>(null)
  const [mirrorPendingRewards, setMirrorPendingRewards] = useState<[bigint, bigint, bigint] | null>(null)
  
  // AGC: Fetch marketplace revenue status
  const fetchMarketplaceStatus = async () => {
    setIsLoadingMarketplace(true)
    try {
      const response = await fetch(buildBackendUrl('/api/revenue-distribution/status'))
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setMarketplaceRevenue(data)
        }
      }
    } catch (error) {
      console.error('Failed to fetch marketplace status:', error)
    } finally {
      setIsLoadingMarketplace(false)
    }
  }
  
  // AGC: Distribute marketplace revenue
  const handleDistributeRevenue = async () => {
    if (!address) return
    
    setIsDistributing(true)
    setDistributeError(null)
    setDistributeSuccess(null)
    
    try {
      const response = await fetch(buildBackendUrl('/api/revenue-distribution/distribute'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-wallet-address': address
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        setDistributeSuccess('✅ Marketplace revenue distributed! Stakers can now claim their rewards.')
        // AGC: Refresh marketplace status
        await fetchMarketplaceStatus()
        // AGC: Refresh week revenue
        setTimeout(() => {
          refetchCurrentWeek()
          refetchWeekRevenue()
        }, 2000)
      } else {
        setDistributeError(data.error || 'Failed to distribute revenue')
      }
    } catch (error: any) {
      setDistributeError(error.message || 'Failed to distribute revenue')
    } finally {
      setIsDistributing(false)
    }
  }
  
  // AGC: Fetch marketplace status on mount
  useEffect(() => {
    if (isConnected) {
      fetchMarketplaceStatus()
    }
  }, [isConnected])

  // AGC: Read revenue share address from StakingManager FIRST
  const { data: revenueShareFromManager } = useReadContract({
    address: STAKING_MANAGER_ADDRESS,
    abi: STAKING_MANAGER_ABI,
    functionName: 'revenueShareContract',
    query: {
      enabled: isConnected && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: `0x${string}` | undefined }

  // AGC: Use revenue share address from manager or fallback to env
  // AGC: Use useMemo to avoid recalculating on every render
  const effectiveRevenueShareAddress = useMemo(() => {
    if (revenueShareFromManager && revenueShareFromManager !== '0x0000000000000000000000000000000000000000') {
      return revenueShareFromManager
    }
    return REVENUE_SHARE_ADDRESS
  }, [revenueShareFromManager]) as `0x${string}`

  // AGC: Check if connected wallet is contract owner of the RevenueShare contract
  // AGC: FIXED: Pass the effectiveRevenueShareAddress to check the correct owner
  const { isOwner, ownerAddress, isLoading: isOwnerLoading } = useContractOwner(
    effectiveRevenueShareAddress !== '0x0000000000000000000000000000000000000000' 
      ? effectiveRevenueShareAddress 
      : undefined
  )

  // AGC: Read current week
  const { data: currentWeek, refetch: refetchCurrentWeek } = useReadContract({
    address: effectiveRevenueShareAddress,
    abi: REVENUE_SHARE_ABI,
    functionName: 'getCurrentWeek',
    query: {
      enabled: isConnected && effectiveRevenueShareAddress !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: bigint | undefined; refetch: () => void }

  // AGC: Read current week revenue status
  const { data: currentWeekRevenue, refetch: refetchWeekRevenue } = useReadContract({
    address: effectiveRevenueShareAddress,
    abi: REVENUE_SHARE_ABI,
    functionName: 'weeklyRevenue',
    args: currentWeek !== undefined ? [currentWeek] : undefined,
    query: {
      enabled: isConnected && currentWeek !== undefined && effectiveRevenueShareAddress !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: [bigint, bigint, bigint, boolean] | undefined; refetch: () => void }

  // AGC: Read StakingMirror pending rewards for current week (V3)
  const { data: mirrorRewardsV3, refetch: refetchMirrorRewardsV3 } = useReadContract({
    address: effectiveRevenueShareAddress,
    abi: REVENUE_SHARE_ABI,
    functionName: 'getPendingRewards',
    args: currentWeek !== undefined ? [STAKING_MIRROR_ADDRESS, currentWeek] : undefined,
    query: {
      enabled: isConnected && currentWeek !== undefined && effectiveRevenueShareAddress !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: [bigint, bigint, bigint] | undefined; refetch: () => void }

  // AGC: Read StakingMirror pending rewards for Week 99 (V2 - old contract)
  const { data: mirrorRewardsV2, refetch: refetchMirrorRewardsV2 } = useReadContract({
    address: REVENUE_SHARE_V2_ADDRESS,
    abi: REVENUE_SHARE_ABI,
    functionName: 'getPendingRewards',
    args: [STAKING_MIRROR_ADDRESS, 99n],
    query: {
      enabled: isConnected && REVENUE_SHARE_V2_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: [bigint, bigint, bigint] | undefined; refetch: () => void }

  // AGC: Update mirror pending rewards state (prioritize V2 for Week 99, V3 for current week)
  useEffect(() => {
    if (mirrorRewardsV2 && (mirrorRewardsV2[0] > 0n || mirrorRewardsV2[1] > 0n || mirrorRewardsV2[2] > 0n)) {
      // AGC: V2 has Week 99 rewards
      setMirrorPendingRewards(mirrorRewardsV2)
    } else if (mirrorRewardsV3) {
      // AGC: V3 for current week
      setMirrorPendingRewards(mirrorRewardsV3)
    }
  }, [mirrorRewardsV2, mirrorRewardsV3])

  const { data: txReceipt, isError: isTxError, error: txError } = useWaitForTransactionReceipt({ 
    hash: pendingTxHash || undefined 
  })

  // AGC: Handle transaction receipt
  React.useEffect(() => {
    if (txReceipt && pendingTxHash) {
      if (txReceipt.status === 'success') {
        setSuccess('✅ Payout anticipated successfully! Transaction confirmed.')
        // AGC: Refetch week status to update UI
        setTimeout(() => {
          refetchCurrentWeek()
          refetchWeekRevenue()
        }, 2000)
      } else {
        setError('Transaction failed on-chain. Check BaseScan for details.')
      }
      setPendingTxHash(null)
      setTargetWeek('')
    }
  }, [txReceipt, pendingTxHash, refetchCurrentWeek, refetchWeekRevenue])

  // AGC: Handle transaction errors
  React.useEffect(() => {
    if (isTxError && txError && pendingTxHash) {
      setError(`Transaction error: ${txError.message || 'Unknown error'}`)
      setPendingTxHash(null)
    }
  }, [isTxError, txError, pendingTxHash])

  // AGC: Check if marketplace has accumulated revenue
  const hasMarketplaceRevenue = marketplaceRevenue?.contracts && (
    (marketplaceRevenue.contracts.buff1155?.hasRevenue) ||
    (marketplaceRevenue.contracts.classicPotion1155?.hasRevenue)
  )

  // AGC: Anticipate payout for current week
  const handleAnticipateCurrentWeek = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (effectiveRevenueShareAddress === '0x0000000000000000000000000000000000000000') {
      setError('Revenue share contract address not configured')
      return
    }

    // AGC: Allow attempting to anticipate even if finalized (contract will reject if already finalized)
    // AGC: This allows owner to try again or anticipate next week
    if (isCurrentWeekFinalized) {
      const confirm = window.confirm(
        '⚠️ Current week is already finalized.\n\n' +
        'The contract will reject this transaction if the week is already finalized.\n\n' +
        'Click OK to proceed anyway, or Cancel to use "Anticipate Specific Week" for a different week.'
      )
      if (!confirm) {
        return
      }
    }

    // AGC: Warn if marketplace revenue exists but hasn't been distributed
    if (hasMarketplaceRevenue) {
      const confirmDistribute = window.confirm(
        '⚠️ Marketplace revenue detected but not yet distributed!\n\n' +
        'It is recommended to distribute marketplace revenue first before anticipating payout.\n\n' +
        'Click OK to proceed anyway, or Cancel to distribute revenue first.'
      )
      if (!confirmDistribute) {
        return
      }
    }

    try {
      setError(null)
      setSuccess(null)
      console.log('🔄 [AnticipatePayout] Calling anticipatePayout()...')
      console.log('   Contract:', effectiveRevenueShareAddress)
      console.log('   Current week:', currentWeek?.toString())
      console.log('   Week finalized:', isCurrentWeekFinalized)

      const hash = await writeContractAsync({
        address: effectiveRevenueShareAddress,
        abi: REVENUE_SHARE_ABI,
        functionName: 'anticipatePayout',
        args: [],
      })

      console.log('✅ [AnticipatePayout] Transaction submitted:', hash)
      setPendingTxHash(hash)
      setSuccess('Transaction submitted! Waiting for confirmation...')
    } catch (err: any) {
      console.error('❌ [AnticipatePayout] Failed to anticipate payout:', err)
      const errorMessage = err.message || err.reason || 'Failed to anticipate payout'
      setError(errorMessage)
      // AGC: Don't clear pendingTxHash if transaction was submitted but failed later
      if (!pendingTxHash) {
        setPendingTxHash(null)
      }
    }
  }

  // AGC: Anticipate payout for specific week
  const handleAnticipateSpecificWeek = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet')
      return
    }

    if (!targetWeek || isNaN(Number(targetWeek))) {
      setError('Please enter a valid week number')
      return
    }

    if (effectiveRevenueShareAddress === '0x0000000000000000000000000000000000000000') {
      setError('Revenue share contract address not configured')
      return
    }

    try {
      setError(null)
      setSuccess(null)

      const week = BigInt(targetWeek)
      const hash = await writeContractAsync({
        address: effectiveRevenueShareAddress,
        abi: REVENUE_SHARE_ABI,
        functionName: 'anticipatePayoutForWeek',
        args: [week],
      })

      setPendingTxHash(hash)
      setSuccess('Transaction submitted! Waiting for confirmation...')
      setTargetWeek('')
    } catch (err: any) {
      console.error('Failed to anticipate payout:', err)
      setError(err.message || 'Failed to anticipate payout')
    }
  }

  // AGC: Claim StakingMirror rewards (from V2 for Week 99, or V3 for current week)
  const handleClaimMirrorRewards = async () => {
    if (!isConnected || !address) {
      setMirrorClaimError('Please connect your wallet')
      return
    }

    if (!isOwner) {
      setMirrorClaimError('Only contract owner can claim rewards for StakingMirror')
      return
    }

    if (!mirrorPendingRewards || (mirrorPendingRewards[0] === 0n && mirrorPendingRewards[1] === 0n && mirrorPendingRewards[2] === 0n)) {
      setMirrorClaimError('No rewards available for StakingMirror')
      return
    }

    // AGC: Determine which contract and week to use
    // AGC: If V2 has rewards (Week 99), use V2. Otherwise use V3 for current week.
    const useV2 = mirrorRewardsV2 && (mirrorRewardsV2[0] > 0n || mirrorRewardsV2[1] > 0n || mirrorRewardsV2[2] > 0n)
    const contractAddress = useV2 ? REVENUE_SHARE_V2_ADDRESS : effectiveRevenueShareAddress
    const targetWeek = useV2 ? 99n : (currentWeek || 0n)

    if (contractAddress === '0x0000000000000000000000000000000000000000') {
      setMirrorClaimError('Revenue share contract address not configured')
      return
    }

    if (!useV2 && !currentWeek) {
      setMirrorClaimError('Current week not available')
      return
    }

    try {
      setIsClaimingMirror(true)
      setMirrorClaimError(null)
      setMirrorClaimSuccess(null)

      console.log('🔄 [AnticipatePayout] Claiming StakingMirror rewards...')
      console.log('   Contract:', contractAddress, useV2 ? '(V2 - Week 99)' : '(V3 - Current Week)')
      console.log('   StakingMirror:', STAKING_MIRROR_ADDRESS)
      console.log('   Recipient (Backend Wallet):', address)
      console.log('   Week:', targetWeek.toString())
      console.log('   Pending Rewards:', {
        ETH: formatEther(mirrorPendingRewards[0]),
        OGB: formatEther(mirrorPendingRewards[1]),
        USDC: formatUnits(mirrorPendingRewards[2], 6)
      })

      const hash = await writeContractAsync({
        address: contractAddress,
        abi: REVENUE_SHARE_ABI,
        functionName: 'claimRewardsForContract',
        args: [STAKING_MIRROR_ADDRESS, address, targetWeek],
      })

      console.log('✅ [AnticipatePayout] Transaction submitted:', hash)
      setPendingTxHash(hash)
      setMirrorClaimSuccess(`Transaction submitted! Rewards will be sent to ${shortenAddress(address)} (from ${useV2 ? 'V2' : 'V3'})`)
      
      // AGC: Wait for transaction and refresh
      setTimeout(() => {
        if (useV2) {
          refetchMirrorRewardsV2()
        } else {
          refetchMirrorRewardsV3()
        }
      }, 5000)
    } catch (err: any) {
      console.error('❌ [AnticipatePayout] Failed to claim StakingMirror rewards:', err)
      setMirrorClaimError(err.message || 'Failed to claim StakingMirror rewards')
    } finally {
      setIsClaimingMirror(false)
    }
  }

  if (!isConnected) {
    return (
      <div className="p-6 bg-slate-900 rounded-xl text-white">
        <h2 className="text-xl font-bold mb-4">⚡ Anticipate Payout</h2>
        <p className="text-slate-400">Please connect your wallet to access this feature.</p>
      </div>
    )
  }

  const isCurrentWeekFinalized = currentWeekRevenue?.[3] === true

  // AGC: Helper function to shorten addresses
  const shortenAddress = (addr: string): string => {
    if (!addr || addr.length < 10) return addr
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`
  }

  return (
    <div className="p-6 bg-slate-900 rounded-xl text-white space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2">⚡ Anticipate Payout - Owner Only</h2>
        <p className="text-sm text-slate-400">
          Finalize the current week early to allow immediate claims. This calculates stake-seconds up to the current timestamp.
        </p>
      </div>

      {/* AGC: Owner Status Badge */}
      {isOwnerLoading ? (
        <div className="p-3 bg-slate-800 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
            <span className="text-sm text-slate-400">Checking owner status...</span>
          </div>
        </div>
      ) : isOwner ? (
        <div className="p-3 bg-green-900/30 border border-green-500 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-green-400 font-bold">✅ Contract Owner</span>
              <span className="text-xs text-slate-400 font-mono">{shortenAddress(address!)}</span>
            </div>
            <span className="text-xs text-green-400">Owner functions enabled</span>
          </div>
        </div>
      ) : (
        <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-red-400 font-semibold">⚠️ Not Contract Owner</span>
              <p className="text-xs text-slate-400 mt-1">
                Contract owner: <span className="font-mono">{ownerAddress ? shortenAddress(ownerAddress) : 'Loading...'}</span>
              </p>
            </div>
            <span className="text-xs text-red-400">Owner functions disabled</span>
          </div>
        </div>
      )}

      {/* AGC: Status Messages */}
      {error && (
        <div className="p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold">⚠️ {error}</p>
              {pendingTxHash && (
                <p className="text-xs text-red-300 mt-1">
                  Transaction hash: <span className="font-mono">{pendingTxHash.slice(0, 10)}...{pendingTxHash.slice(-8)}</span>
                </p>
              )}
            </div>
            {pendingTxHash && (
              <a
                href={`https://sepolia.basescan.org/tx/${pendingTxHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline text-sm ml-4"
              >
                View on BaseScan
              </a>
            )}
          </div>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-900/50 border border-green-500 rounded-lg text-green-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold">✅ {success}</p>
              {pendingTxHash && (
                <p className="text-xs text-green-300 mt-1">
                  Transaction hash: <span className="font-mono">{pendingTxHash.slice(0, 10)}...{pendingTxHash.slice(-8)}</span>
                </p>
              )}
            </div>
            {pendingTxHash && (
              <a
                href={`https://sepolia.basescan.org/tx/${pendingTxHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline text-sm ml-4"
              >
                View on BaseScan
              </a>
            )}
          </div>
        </div>
      )}
      {isPending && pendingTxHash && (
        <div className="p-3 bg-blue-900/50 border border-blue-500 rounded-lg text-blue-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold">⏳ Transaction pending...</p>
              <p className="text-xs text-blue-300 mt-1">
                Hash: <span className="font-mono">{pendingTxHash.slice(0, 10)}...{pendingTxHash.slice(-8)}</span>
              </p>
              <p className="text-xs text-blue-300 mt-1">Waiting for blockchain confirmation...</p>
            </div>
            <a
              href={`https://sepolia.basescan.org/tx/${pendingTxHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline text-sm ml-4"
            >
              View on BaseScan
            </a>
          </div>
        </div>
      )}

      {/* AGC: Current Week Info */}
      {currentWeek !== undefined && (
        <section className="border border-slate-700 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold">📅 Current Week</h3>
            <button
              onClick={() => {
                refetchCurrentWeek()
                refetchWeekRevenue()
              }}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium transition"
              title="Refresh week status"
            >
              🔄 Refresh
            </button>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
              <span className="text-slate-400">Week Number:</span>
              <span className="font-bold">{currentWeek.toString()}</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
              <span className="text-slate-400">Status:</span>
              <span className={`font-semibold ${isCurrentWeekFinalized ? 'text-green-400' : 'text-yellow-400'}`}>
                {isCurrentWeekFinalized ? '✅ Finalized' : '⏳ Pending'}
              </span>
            </div>
            {currentWeekRevenue && (
              <>
                <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
                  <span className="text-slate-400">ETH Revenue:</span>
                  <span className="font-mono">{formatEther(currentWeekRevenue[0])} ETH</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
                  <span className="text-slate-400">OGB Revenue:</span>
                  <span className="font-mono">{formatEther(currentWeekRevenue[1])} OGB</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-slate-800 rounded">
                  <span className="text-slate-400">USDC Revenue:</span>
                  <span className="font-mono">{formatUnits(currentWeekRevenue[2], 6)} USDC</span>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* AGC: Marketplace Revenue Distribution */}
      <section className="border border-amber-700/50 bg-amber-900/10 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-amber-400">🏪 Marketplace Revenue</h3>
          <button
            onClick={fetchMarketplaceStatus}
            disabled={isLoadingMarketplace}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium transition disabled:opacity-50"
          >
            {isLoadingMarketplace ? '⏳' : '🔄'} Refresh
          </button>
        </div>
        
        <p className="text-sm text-slate-400 mb-4">
          Distribute accumulated marketplace revenue (Buffs & Potions) directly to stakers.
          <br />
          <span className="text-green-400">✅ This also triggers anticipatePayout() - stakers can claim immediately!</span>
          <br />
          <span className="text-amber-400">⏰ Auto-distribution: Every Sunday 23:50 UTC</span>
        </p>
        
        {/* AGC: Distribution status messages */}
        {distributeError && (
          <div className="p-2 bg-red-900/50 border border-red-500 rounded mb-3 text-red-200 text-sm">
            ⚠️ {distributeError}
          </div>
        )}
        {distributeSuccess && (
          <div className="p-2 bg-green-900/50 border border-green-500 rounded mb-3 text-green-200 text-sm">
            {distributeSuccess}
          </div>
        )}
        
        {/* AGC: Marketplace balances */}
        {marketplaceRevenue?.contracts && (
          <div className="space-y-3 mb-4">
            {/* Buff1155 */}
            {marketplaceRevenue.contracts.buff1155 && (
              <div className="p-3 bg-slate-800 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-blue-400">🛡️ Buff1155</span>
                  <span className={`text-xs px-2 py-1 rounded ${marketplaceRevenue.contracts.buff1155.hasRevenue ? 'bg-green-600' : 'bg-slate-600'}`}>
                    {marketplaceRevenue.contracts.buff1155.hasRevenue ? '💰 Has Revenue' : 'Empty'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-slate-700 p-2 rounded">
                    <span className="text-slate-400">ETH:</span>
                    <span className="ml-1 font-mono">{marketplaceRevenue.contracts.buff1155.ethBalance}</span>
                  </div>
                  <div className="bg-slate-700 p-2 rounded">
                    <span className="text-slate-400">OGB:</span>
                    <span className="ml-1 font-mono">{marketplaceRevenue.contracts.buff1155.ogbBalance}</span>
                  </div>
                  <div className="bg-slate-700 p-2 rounded">
                    <span className="text-slate-400">USDC:</span>
                    <span className="ml-1 font-mono">{marketplaceRevenue.contracts.buff1155.usdcBalance}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* ClassicPotion1155 */}
            {marketplaceRevenue.contracts.classicPotion1155 && (
              <div className="p-3 bg-slate-800 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-pink-400">🧪 ClassicPotion1155</span>
                  <span className={`text-xs px-2 py-1 rounded ${marketplaceRevenue.contracts.classicPotion1155.hasRevenue ? 'bg-green-600' : 'bg-slate-600'}`}>
                    {marketplaceRevenue.contracts.classicPotion1155.hasRevenue ? '💰 Has Revenue' : 'Empty'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-slate-700 p-2 rounded">
                    <span className="text-slate-400">ETH:</span>
                    <span className="ml-1 font-mono">{marketplaceRevenue.contracts.classicPotion1155.ethBalance}</span>
                  </div>
                  <div className="bg-slate-700 p-2 rounded">
                    <span className="text-slate-400">OGB:</span>
                    <span className="ml-1 font-mono">{marketplaceRevenue.contracts.classicPotion1155.ogbBalance}</span>
                  </div>
                  <div className="bg-slate-700 p-2 rounded">
                    <span className="text-slate-400">USDC:</span>
                    <span className="ml-1 font-mono">{marketplaceRevenue.contracts.classicPotion1155.usdcBalance}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* AGC: No marketplace contracts configured */}
        {marketplaceRevenue && !marketplaceRevenue.contracts.buff1155 && !marketplaceRevenue.contracts.classicPotion1155 && (
          <div className="p-3 bg-slate-800 rounded-lg mb-4 text-center text-slate-400">
            ℹ️ No marketplace contracts configured
          </div>
        )}
        
        {/* AGC: Loading state */}
        {isLoadingMarketplace && !marketplaceRevenue && (
          <div className="p-3 bg-slate-800 rounded-lg mb-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500 mx-auto mb-2"></div>
            <span className="text-slate-400">Loading marketplace status...</span>
          </div>
        )}
        
        {/* AGC: Distribute button */}
        <button
          onClick={handleDistributeRevenue}
          disabled={isDistributing || !isOwner || !marketplaceRevenue?.initialized}
          className="w-full py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-bold text-lg transition"
        >
          {isDistributing 
            ? '⏳ Distributing to Stakers...' 
            : !isOwner 
              ? 'Owner Only' 
              : !marketplaceRevenue?.initialized 
                ? 'Service Not Ready' 
                : '💰 Distribute to Stakers Now'}
        </button>
        
        <p className="text-xs text-slate-500 mt-2 text-center">
          ℹ️ Distributes marketplace revenue AND calls anticipatePayout() - stakers can claim immediately
        </p>
      </section>

      {/* AGC: Anticipate Current Week */}
      <section className="border border-slate-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">⚡ Anticipate Current Week</h3>
        <p className="text-sm text-slate-400 mb-4">
          Finalize the current week ({currentWeek?.toString() || 'N/A'}) early. This will allow stakers to claim rewards immediately.
        </p>
        
        {/* AGC: Warning if marketplace revenue exists but not distributed */}
        {hasMarketplaceRevenue && isOwner && !isCurrentWeekFinalized && (
          <div className="p-3 bg-amber-900/30 border border-amber-500 rounded-lg mb-4">
            <p className="text-amber-400 text-sm font-semibold mb-1">
              ⚠️ Marketplace Revenue Pending Distribution
            </p>
            <p className="text-amber-300 text-xs">
              You have accumulated marketplace revenue that hasn't been distributed yet.
              <br />
              <strong>Recommended:</strong> Click "Distribute to Stakers Now" above - it will distribute AND anticipate payout in one step.
            </p>
          </div>
        )}
        
        {!isOwner && (
          <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg mb-4">
            <p className="text-red-400 text-sm">
              ⚠️ Only the contract owner can anticipate payouts.
            </p>
          </div>
        )}
        <button
          onClick={handleAnticipateCurrentWeek}
          disabled={isPending || !currentWeek || !isOwner}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-bold text-lg transition"
        >
          {isPending ? 'Processing...' : !isOwner ? 'Owner Only' : isCurrentWeekFinalized ? '⚡ Anticipate Current Week (Already Finalized - Will Fail)' : '⚡ Anticipate Current Week'}
        </button>
      </section>

      {/* AGC: Anticipate Specific Week */}
      <section className="border border-slate-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">📅 Anticipate Specific Week</h3>
        <p className="text-sm text-slate-400 mb-4">
          Finalize a specific past week early. Only weeks up to the current week can be finalized.
        </p>
        <div className="flex gap-2">
          <input
            type="number"
            value={targetWeek}
            onChange={(e) => setTargetWeek(e.target.value)}
            placeholder="Week number"
            className="flex-1 px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-lg"
            min="0"
          />
          <button
            onClick={handleAnticipateSpecificWeek}
            disabled={isPending || !targetWeek || !isOwner}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold transition"
          >
            {isPending ? 'Processing...' : !isOwner ? 'Owner Only' : 'Finalize Week'}
          </button>
        </div>
      </section>

      {/* AGC: Claim StakingMirror Rewards */}
      <section className="border border-cyan-700/50 bg-cyan-900/10 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-cyan-400">🪞 Claim StakingMirror Rewards</h3>
          <button
            onClick={() => {
              refetchMirrorRewardsV2()
              refetchMirrorRewardsV3()
            }}
            disabled={isClaimingMirror}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium transition disabled:opacity-50"
          >
            🔄 Refresh
          </button>
        </div>
        
        <p className="text-sm text-slate-400 mb-4">
          Claim rewards on behalf of StakingMirror contract (which cannot sign transactions).
          <br />
          <span className="text-cyan-400">Rewards will be sent to your wallet (backend wallet).</span>
          <br />
          <span className="text-amber-400">⚠️ Week 99 rewards are in V2 (old contract). Future weeks use V3.</span>
        </p>

        {/* AGC: StakingMirror pending rewards display */}
        {mirrorPendingRewards && (
          <div className="p-3 bg-slate-800 rounded-lg mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-cyan-400">🪞 StakingMirror</span>
              <span className={`text-xs px-2 py-1 rounded ${
                (mirrorPendingRewards[0] > 0n || mirrorPendingRewards[1] > 0n || mirrorPendingRewards[2] > 0n) 
                  ? 'bg-green-600' 
                  : 'bg-slate-600'
              }`}>
                {(mirrorPendingRewards[0] > 0n || mirrorPendingRewards[1] > 0n || mirrorPendingRewards[2] > 0n) 
                  ? '💰 Has Rewards' 
                  : 'No Rewards'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-slate-700 p-2 rounded">
                <span className="text-slate-400">ETH:</span>
                <span className="ml-1 font-mono">{formatEther(mirrorPendingRewards[0])}</span>
              </div>
              <div className="bg-slate-700 p-2 rounded">
                <span className="text-slate-400">OGB:</span>
                <span className="ml-1 font-mono">{formatEther(mirrorPendingRewards[1])}</span>
              </div>
              <div className="bg-slate-700 p-2 rounded">
                <span className="text-slate-400">USDC:</span>
                <span className="ml-1 font-mono">{formatUnits(mirrorPendingRewards[2], 6)}</span>
              </div>
            </div>
            {mirrorPendingRewards && (
              <p className="text-xs text-slate-500 mt-2">
                {mirrorRewardsV2 && (mirrorRewardsV2[0] > 0n || mirrorRewardsV2[1] > 0n || mirrorRewardsV2[2] > 0n) 
                  ? `Week: 99 (V2 - Old Contract)` 
                  : `Week: ${currentWeek?.toString() || 'N/A'} (V3 - New Contract)`} 
                | StakingMirror: {shortenAddress(STAKING_MIRROR_ADDRESS)}
              </p>
            )}
          </div>
        )}

        {/* AGC: Status messages */}
        {mirrorClaimError && (
          <div className="p-2 bg-red-900/50 border border-red-500 rounded mb-3 text-red-200 text-sm">
            ⚠️ {mirrorClaimError}
          </div>
        )}
        {mirrorClaimSuccess && (
          <div className="p-2 bg-green-900/50 border border-green-500 rounded mb-3 text-green-200 text-sm">
            ✅ {mirrorClaimSuccess}
          </div>
        )}

        {/* AGC: Claim button */}
        <button
          onClick={handleClaimMirrorRewards}
          disabled={isClaimingMirror || !isOwner || !mirrorPendingRewards || (mirrorPendingRewards[0] === 0n && mirrorPendingRewards[1] === 0n && mirrorPendingRewards[2] === 0n) || !currentWeek}
          className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-bold text-lg transition"
        >
          {isClaimingMirror 
            ? '⏳ Claiming Rewards...' 
            : !isOwner 
              ? 'Owner Only' 
              : !mirrorPendingRewards || (mirrorPendingRewards[0] === 0n && mirrorPendingRewards[1] === 0n && mirrorPendingRewards[2] === 0n)
                ? 'No Rewards Available' 
                : '🪞 Claim StakingMirror Rewards'}
        </button>
        
        <p className="text-xs text-slate-500 mt-2 text-center">
          ℹ️ Rewards will be sent to your wallet (backend wallet: {address ? shortenAddress(address) : 'N/A'})
        </p>
      </section>
    </div>
  )
}

export default AnticipatePayout

